import React, { useEffect, useRef, useState } from 'react';
import './LocationComponent.scss';
import { ErrorMessage, Field, Form, Formik, useFormikContext } from 'formik';
import { useDispatch } from 'react-redux';
import { setLocationListing } from '../../../redux/slices/CreateLisingSlice';
import CustomMapComponent from '../../ListingsComponents/CustomMapComponent';
import FormikFieldAutoComplete from '../../../utils/FormikFieldAutoComplete';
import DisableSection from '../../../utils/DisableSection/DisableSection';
import { ListingSection } from '../../../constants/ListingSection';
import { BsPencilFill } from 'react-icons/bs';
import { ListingLocationSchema } from '../../../utils/yupSchemas/CreateListingSchema';
import { createMultipartFormData } from '../../../utils/createMultipartFormdata';
import { IoCloseCircleOutline } from 'react-icons/io5';
import { setFormDirty } from '../../../redux/slices/LocationSlice';
import { useStateCity } from '../../../hooks/useStateCity';
import FormikFormSelectField from '../../../utils/FormikFormSelect';
import FormikFormInput from '../../../utils/FormikFormInput';

function LocationComponent({
  isEdit,
  handleSubmit,
  currentSection,
  setCurrentSection,
  initialValues,
  savedSection,
  dealType,
}: {
  isEdit?: boolean;
  handleSubmit: (formData: FormData) => void;
  currentSection: ListingSection;
  setCurrentSection: any;
  initialValues: any;
  savedSection?: any;
  dealType: 'rent' | 'sell';
}) {
  const dispatch = useDispatch();
  const [state, setState] = useState('');
  const stateRef = useRef(initialValues?.location?.state?.value);
  const [allCity, setAllCity] = useState<any>([]);
  const [allState, setAllState] = useState<any>([]);
  const { loading, states, stateLoading, cities } = useStateCity({
    stateCode: state,
  });

  useEffect(() => {
    if (states?.length) {
      const state = states?.map((e) => {
        return { value: e.code, label: e.name };
      });
      setAllState(state);
    }
  }, [states]);

  useEffect(() => {
    if (cities.length) {
      const city = cities?.map((e, i) => {
        return { value: `${e.code}_${i}`, label: e.name };
      });
      setAllCity(city);
    }
  }, [cities]);

  useEffect(() => {
    if (initialValues?.location?.state?.value) {
      setState(initialValues?.location?.state?.value);
    }
  }, [initialValues?.location?.state?.value]);

  const submitHandler = (values: any) => {
    let newProperty: any = {
      section: values.section,
    };
    const formData = new FormData();
    const data = { ...values.location };
    newProperty = {
      dealType,
      ...newProperty,
      address: {
        addressLine1: data.addressLine1,
        code: data.state.value,
        street: data.street,
        city: data.city.label,
        state: data.state.label,
        zip: data.zip,
        latitude: data.latitude ?? '',
        longitude: data.longitude ?? '',
      },
      is_active: false,
    };
    // Create Form
    createMultipartFormData(newProperty, formData);
    handleSubmit(formData);
    dispatch(setLocationListing({ address: newProperty?.address }));
  };

  const FormObject = () => {
    const { values, setFieldValue, dirty, isSubmitting } =
      useFormikContext<any>();

    useEffect(() => {
      if (dirty && !isSubmitting) {
        dispatch(setFormDirty(dirty));
      }
    }, [dirty, isSubmitting]);

    useEffect(() => {
      if (
        values.location.addressLine1 ||
        values.location.street ||
        values.location.city ||
        values.location.state ||
        values.location.zip
      ) {
        setFieldValue('location.addressLine1', values.location.addressLine1);
        setFieldValue('location.street', values.location.street);
        setFieldValue('location.city', values.location.city);
        setFieldValue('location.state', values.location.state);
        setFieldValue('location.zip', values.location.zip);
      }
    }, [
      values.location.addressLine1,
      values.location.street,
      values.location.city,
      values.location.state,
      values.location.zip,
    ]);
    return null;
  };

  const FormValue = () => {
    const { values, setFieldValue } = useFormikContext<any>();
    useEffect(() => {
      if (
        values.location.state.value !== '' &&
        stateRef.current !== '' &&
        values.location.state.value !== stateRef.current
      ) {
        setState(values.location.state.value);
        stateRef.current = values.location.state.value;
        setFieldValue('location.city', { value: '', label: '' });
      } else if (values.location.state.value !== '') {
        setState(values.location.state.value);
        stateRef.current = values.location.state.value;
      }
    }, [values.location.state]);

    return null;
  };

  return (
    <Formik
      initialValues={{
        ...initialValues,
        section: ListingSection.Location,
      }}
      validationSchema={ListingLocationSchema}
      enableReinitialize={true}
      onSubmit={submitHandler}
    >
      {({ values, setFieldValue, touched, errors, resetForm }: any) => (
        <Form>
          <FormObject />
          <FormValue />
          <div id="location">
            {currentSection !== ListingSection.Location && <DisableSection />}
            <div className="box-view">
              <h3 className="box-title">Location</h3>
              {(isEdit || savedSection[ListingSection.Location]) && (
                <div title="Edit" className="edit-property">
                  {currentSection !== ListingSection.Location && (
                    <button
                      type="button"
                      className="edit-btn-listing"
                      onClick={() => {
                        setCurrentSection(ListingSection.Location);
                      }}
                    >
                      <BsPencilFill className="pencil_icon" />
                    </button>
                  )}
                  {currentSection === ListingSection.Location && (
                    <button
                      type="button"
                      className="edit-btn-listing"
                      onClick={() => {
                        setCurrentSection(-1);
                        resetForm();
                      }}
                    >
                      <IoCloseCircleOutline className="cancel_icon" />
                    </button>
                  )}
                </div>
              )}
              <div className="row">
                <div className="col-12 mb-3">
                  <label className="form-label" htmlFor="line1">
                    Flat, House no., Building, Company, Apartment
                  </label>
                  <Field
                    type="text"
                    name="location.addressLine1"
                    placeholder=""
                    autoComplete="off"
                    className={`form-control ${
                      touched.addressLine1 && errors.addressLine1
                        ? 'is-invalid'
                        : ''
                    }`}
                    as={FormikFormInput}
                  />
                </div>

                <div className="col-12 mb-3">
                  <label className="form-label" htmlFor="street">
                    Area, Street, Sector, Village
                  </label>
                  <Field
                    type="text"
                    name="location.street"
                    placeholder=""
                    autoComplete="off"
                    className={`form-control ${
                      touched.street && errors.street ? 'is-invalid' : ''
                    }`}
                    as={FormikFormInput}
                  />
                </div>

                <div className="col-md-4 col-sm-6 mb-3">
                  {/* <label className="form-label" htmlFor="state">
                    State
                  </label>
                  <Field
                    type="text"
                    name="location.state"
                    placeholder=""
                    // autoComplete="off"
                    className={`form-control ${
                      touched.state && errors.state ? 'is-invalid' : ''
                    }`}
                  />
                  <ErrorMessage
                    component="div"
                    name="location.state"
                    className="invalid-feedback"
                  /> */}
                  <label className="form-label" htmlFor="state">
                    State
                  </label>
                  <Field
                    as={FormikFormSelectField}
                    isLabelValueSame={true}
                    id="state"
                    name="location.state"
                    className={`form-control ${
                      touched.state && errors.state ? 'is-invalid' : ''
                    }`}
                    options={allState}
                    loading={stateLoading}
                  />
                </div>
                <div className="col-md-4 col-sm-6 mb-3">
                  <label className="form-label" htmlFor="city">
                    City
                  </label>
                  <Field
                    as={FormikFormSelectField}
                    isLabelValueSame={true}
                    id="city"
                    name="location.city"
                    className={`form-control ${
                      touched.city && errors.city ? 'is-invalid' : ''
                    }`}
                    options={allCity}
                    loading={loading}
                  />
                  {/* <label className="form-label" htmlFor="city">
                    City
                  </label>
                  <Field
                    type="text"
                    name="location.city"
                    placeholder=""
                    // autoComplete="off"
                    className={`form-control ${
                      touched.city && errors.city ? 'is-invalid' : ''
                    }`}
                  />
                  <ErrorMessage
                    component="div"
                    name="location.city"
                    className="invalid-feedback"
                  /> */}
                </div>
                <div className="col-md-4 col-sm-6 mb-3">
                  <label className="form-label" htmlFor="zip">
                    Zip
                  </label>
                  <Field
                    type="number"
                    name="location.zip"
                    placeholder=""
                    // autoComplete="off"
                    className={`form-control ${
                      touched.zip && errors.zip ? 'is-invalid' : ''
                    }`}
                  />
                  <ErrorMessage
                    component="div"
                    name="location.zip"
                    className="invalid-feedback"
                  />
                </div>
                {/* <LoadScript googleMapsApiKey={`${process.env.REACT_APP_GOOGLE_API_KEY}` ?? ''} libraries={["places"]}> */}
                {/* <div className="h-418px" style={{ width: '100%' }}>
                  <CustomMapComponent
                    mapClassName={''}
                    markers={[
                      {
                        lat: values.location.latitude
                          ? parseFloat(values.location.latitude)
                          : values.location.latitude,
                        lng: values.location.longitude
                          ? parseFloat(values.location.longitude)
                          : values.location.longitude,
                        propertyName: '',
                      },
                    ]}
                  />
                </div> */}
                {/* </LoadScript> */}
                <div id="amenitiesTab" className="row my-3">
                  {isEdit && currentSection === ListingSection.Location && (
                    <div className="col-12 text-center">
                      <a href="#createListingTab">
                        <div
                          // href="#create-listing"
                          className="btn btn-outline-primary me-2"
                          onClick={() =>
                            setCurrentSection(ListingSection.Details)
                          }
                        >
                          Previous
                        </div>
                      </a>
                      <a href="#amenitiesTab">
                        <button type="submit" className="btn btn-primary">
                          Update
                        </button>
                      </a>
                    </div>
                  )}
                  {!isEdit && currentSection === ListingSection.Location && (
                    <div className="col-12 text-center">
                      <a href="#createListingTab">
                        <div
                          // href="#create-listing"
                          className="btn btn-outline-primary me-2"
                          onClick={() =>
                            setCurrentSection(ListingSection.Details)
                          }
                        >
                          Previous
                        </div>
                      </a>
                      <a href="#amenitiesTab">
                        <button
                          type="submit"
                          className="btn btn-primary"
                          // onClick={() => {
                          //   handleNext();
                          //   // submit?.click();
                          // }}
                        >
                          Save Draft
                        </button>
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
}

export default LocationComponent;
